import React from 'react'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { ScrollView } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { useSelector } from 'react-redux'
import { StackNavigationProp } from '@react-navigation/stack'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Feature, useFeatureFlag } from '@components'
import { Icon } from '@src/components/base/Icon'
import { NavigationContainer } from '@src/screens/Common/containers'
import { TypesSection } from '@screens/Settings/components/Integrations/DailyMeasurements/TypesSection'
import { ketoMojoSyncStatusSelector } from '@src/selectors/integrations'
import { IconSources } from '@src/assets/icons'
import { User } from '@src/utils'
import { MobileAppFeature, SensorModel } from '@src/types'
import { CommonAuthorizedStackParamList } from '@src/navigation/types'
import { TerraProviders } from './Integrations/TerraProviders'
import { IntegrationTouchableSection } from './Integrations/IntegrationTouchableSection'

export const IntegrationSettings = () => {
  const styles = useStyleSheet(themedStyles)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const authorizedNavigation = useNavigation<StackNavigationProp<CommonAuthorizedStackParamList>>()

  const ketoMojoSync = useSelector(ketoMojoSyncStatusSelector)
  const terraKetoMojoFlag = useFeatureFlag(Feature.TerraKetoMojo)
  const hasSteloApiAccess = User.hasFeature(MobileAppFeature.DexcomSteloApiIntegration)
  const hasDexcomG7ApiAccess = User.hasFeature(MobileAppFeature.DexcomG7ApiIntegration)
  const hasDexcomG6ApiAccess = User.hasFeature(MobileAppFeature.DexcomG6ApiIntegration)
  const isActivateOwnSensorEnabled = User.hasFeature(MobileAppFeature.ActivateOwnSensor)

  const showOldKetoMojoIntegration = ketoMojoSync || !terraKetoMojoFlag

  return (
    <NavigationContainer title="Integrations" showLoadingIndicator>
      <ScrollView
        style={styles.container}
        contentContainerStyle={{ flexGrow: 1, paddingBottom: insets.bottom + 16 }}
      >
        {hasSteloApiAccess && (
          <IntegrationTouchableSection
            sectionTitle="Dexcom Stelo"
            imageSource={IconSources.steloLogo}
            onPress={() => {
              return navigation.navigate('DexcomSensorSettings', {
                sensorModel: SensorModel.DexcomStelo,
              })
            }}
          />
        )}
        {isActivateOwnSensorEnabled && (
          <IntegrationTouchableSection
            sectionTitle="Integrate Own Sensor"
            accessoryLeft={<Icon name="plus" size={24} weight="bold" style={styles.plusIcon} />}
            onPress={() =>
              authorizedNavigation.navigate('OwnSensorSelection', {
                nextScreen: 'ConfirmSensorSelection',
              })
            }
          />
        )}
        {hasDexcomG7ApiAccess && (
          <IntegrationTouchableSection
            sectionTitle="Dexcom G7"
            imageSource={IconSources.dexcomG7Logo}
            onPress={() =>
              navigation.navigate('DexcomSensorSettings', {
                sensorModel: SensorModel.DexcomG7,
              })
            }
          />
        )}
        {hasDexcomG6ApiAccess && (
          <IntegrationTouchableSection
            sectionTitle="Dexcom G6"
            imageSource={IconSources.dexcomG6Logo}
            onPress={() =>
              navigation.navigate('DexcomSensorSettings', {
                sensorModel: SensorModel.DexcomG6,
              })
            }
          />
        )}
        <TerraProviders />
        {showOldKetoMojoIntegration && (
          <IntegrationTouchableSection
            sectionTitle="Keto-Mojo"
            imageSource={IconSources.ketomojo}
            onPress={() => navigation.navigate('KetoMojoIntegration')}
          />
        )}
        <TypesSection />
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    backgroundColor: 'theme.background',
  },
  plusIcon: {
    color: 'theme.text.primary',
    marginRight: 12,
  },
})
